import type { ImportCategoryRow } from '@api/categories-api';
import { parseCsvHeaders, splitCsvLine } from '@utils/csv-utils';

const EXPECTED_HEADERS = ['id', 'base_name', 'is_clinic'] as const;
const MAX_MARKETPLACE_IDS = 10;

function parseBoolean(raw: string): boolean {
  return raw === 'true' || raw === '1';
}

export function extractLocales(headers: string[]): string[] {
  const locales = new Set<string>();
  for (const header of headers) {
    const match = header.match(/^([a-z]{2}-[A-Z]{2})\s/);
    if (match?.[1]) {
      locales.add(match[1]);
    }
  }
  return [...locales];
}

export function parseCategoryRows(content: string): ImportCategoryRow[] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const { headers, delimiter } = parseCsvHeaders(lines[0]!);
  const locales = extractLocales(headers);

  const headerIndex = new Map(headers.map((h, i) => [h, i]));

  const getValue = (fields: string[], header: string): string =>
    fields[headerIndex.get(header) ?? -1]?.trim() ?? '';

  // Optional column: files written before it existed must import unchanged, and a
  // category marked global by hand must survive one of those imports. Absent means
  // "leave as is", which is why it stays undefined rather than defaulting to false.
  const hasGlobalColumn = headerIndex.has('is_global');

  return lines.slice(1).reduce<ImportCategoryRow[]>((acc, line) => {
    const fields = splitCsvLine(line, delimiter);
    const idRaw = getValue(fields, 'id');
    const externalId = Number(idRaw);
    const baseName = getValue(fields, 'base_name');
    const isClinic = parseBoolean(getValue(fields, 'is_clinic'));
    const isGlobal = hasGlobalColumn ? parseBoolean(getValue(fields, 'is_global')) : undefined;

    if (!baseName.trim() || !Number.isFinite(externalId) || externalId <= 0) return acc;

    const details = locales
      .map((locale) => {
        const name = getValue(fields, `${locale} name`);
        const mp1Raw = getValue(fields, `${locale} marketplace_id1`);
        const marketplaceId1 = mp1Raw ? Number(mp1Raw) : 0;

        if (!name) return null;

        const detail: Record<string, string | number> = {
          locale,
          name,
          marketplaceId1,
        };

        for (let i = 2; i <= MAX_MARKETPLACE_IDS; i++) {
          const value = getValue(fields, `${locale} marketplace_id${i}`);
          if (value) detail[`marketplaceId${i}`] = Number(value);
        }

        return detail;
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);

    acc.push({
      externalId,
      baseName,
      isClinic,
      ...(isGlobal !== undefined && { isGlobal }),
      details,
    } as unknown as ImportCategoryRow);
    return acc;
  }, []);
}

export function validateCategoryHeaders(content: string): string | null {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return 'The file must have a header row and at least one data row.';

  const { headers } = parseCsvHeaders(lines[0]!);

  for (const expected of EXPECTED_HEADERS) {
    if (!headers.includes(expected)) {
      return `Missing required column: "${expected}"`;
    }
  }

  const locales = extractLocales(headers);
  if (locales.length === 0) {
    return 'No locale columns found. Expected format: "{locale} name" (e.g. "es-ES name")';
  }

  return null;
}
